import { useLayoutEffect, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { Alert } from '@interstate/components/Alert';
import { Typography } from '@interstate/components/Typography';

import { CDL_V3_EVENT } from '@makemydeal/dr-shared-types';
import { SCI_CDL_TAGS } from '@makemydeal/dr-sales-checkin-types';

import { DDJ_PRELOADER_ID } from '../constants';
import '../custom.d';
import { DDJWrapper } from './digitalDealJacket.style';

type DigitalDealJacketProps = {
    externalId?: string;
};

const DigitalDealJacket = ({ externalId }: DigitalDealJacketProps) => {
    const dispatch = useDispatch();
    const [error, setError] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const pbcElementRef = useRef<Element | null>(null);
    const hasSentViewTag = useRef(false);

    // useLayoutEffect ensures cleanup runs synchronously BEFORE React removes DOM nodes,
    // allowing moveBefore to work since both nodes are still connected.
    useLayoutEffect(() => {
        const preloader = document.getElementById(DDJ_PRELOADER_ID);
        const preloaderStatus = preloader?.getAttribute('data-status');
        const preloadedPbc = preloader?.querySelector('ddj-app');
        let usedPreloader = false;

        if (preloaderStatus === 'error') {
            setError(true);
            return;
        }

        if (preloadedPbc && containerRef.current) {
            pbcElementRef.current = preloadedPbc;

            if (externalId) {
                preloadedPbc.setAttribute('external-id', externalId);
            }

            if ((containerRef.current as any).moveBefore) {
                (containerRef.current as any).moveBefore(preloadedPbc, null);
            } else {
                containerRef.current.appendChild(preloadedPbc);
            }
            preloadedPbc.setAttribute('data-testid', 'digital-deal-jacket-pbc');
            usedPreloader = true;

            // dc-sci-075: SCI-only. Ref-guarded — an externalId change re-runs this effect and would double-count.
            if (!hasSentViewTag.current) {
                hasSentViewTag.current = true;
                dispatch({ type: CDL_V3_EVENT, payload: SCI_CDL_TAGS['dc-sci-075']() });
            }
        } else if (!preloadedPbc) {
            setError(true);
        }

        return () => {
            if (usedPreloader) {
                const currentPreloader = document.getElementById(DDJ_PRELOADER_ID);
                const pbcElement = pbcElementRef.current;

                if (currentPreloader && pbcElement) {
                    pbcElement.setAttribute('data-testid', 'ddj-preloaded-pbc');
                    if ((currentPreloader as any).moveBefore) {
                        (currentPreloader as any).moveBefore(pbcElement, null);
                    } else {
                        currentPreloader.appendChild(pbcElement);
                    }
                }
                pbcElementRef.current = null;
            }
        };
    }, [externalId, dispatch]);

    return (
        <DDJWrapper>
            {error && (
                <Alert type="caution" data-testid="ddj-alert">
                    <Typography variant="h6" sx={{ marginBottom: '2px' }}>
                        An Error has Occurred
                    </Typography>
                    <Typography variant="body-sm" sx={{ marginBottom: '2px' }}>
                        Sorry, the system is currently unable to process your request.
                    </Typography>
                    <Typography variant="body-sm">Please try again later.</Typography>
                </Alert>
            )}
            {!error && <div ref={containerRef} data-testid="ddj-pbc-container"></div>}
        </DDJWrapper>
    );
};

export default DigitalDealJacket;
